import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { totpSchema, type TotpFormData } from '@/lib/validation';
import { authApi, ApiError } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { runInBackground } from '@/lib/utils';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import {
    MfaChallengeCard,
    MfaChallengeLoadingCard,
} from '@/components/auth/MfaChallengeCard';
import {
    MfaSetupStatusResponseSchema,
    MfaMethod,
    SessionUserProfileDetailedResponseSchema,
    VerifyMfaLoginResponseSchema,
    VerifyTotpResponseSchema,
} from '@pillar-portal/api-types';

function parseAvailableMfaMethods(value: string | null): MfaMethod[] {
    if (value === null || value.trim().length === 0) {
        return ['TOTP'];
    }
    const parsedMethods = value
        .split(',')
        .map((method) => method.trim())
        .filter((method): method is MfaMethod =>
            method === 'TOTP' || method === 'EMAIL' || method === 'SMS'
        );
    return parsedMethods.length > 0 ? parsedMethods : ['TOTP'];
}

export default function TotpPage() {
    const log = createComponentLogger('TotpPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const returnToPayment: boolean = searchParams.get('return_to_payment') === '1';
    const paymentRequestUuid: string = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const availableMfaMethods: MfaMethod[] = parseAvailableMfaMethods(
        searchParams.get('mfa_methods')
    );
    const queryClient = useQueryClient();
    const [error, setError] = useState<string>('');
    const [message, setMessage] = useState<string>('');
    const [selectedMethod, setSelectedMethod] = useState<MfaMethod>(
        availableMfaMethods[0] ?? 'TOTP'
    );
    const [isRedirecting, setIsRedirecting] = useState(false);
    const setUser = useAuthStore((state) => state.setUser);

    const setupStatusQuery = useQuery({
        queryKey: ['loginMfaSetupStatusProbe'],
        queryFn: async () => {
            const response = await authApi.getMfaSetupStatus();
            return MfaSetupStatusResponseSchema.parse(response);
        },
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
    });

    useEffect(() => {
        if (setupStatusQuery.data === undefined) {
            return;
        }
        const nextSearchParams = new URLSearchParams();
        if (paymentRequestUuid.length > 0) {
            nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
        }
        if (returnToPayment) {
            nextSearchParams.set('return_to_payment', '1');
        }
        const setupPath = buildRequiredTotpSetupPath(
            setupStatusQuery.data.activation_target ?? undefined
        );
        const setupUrl = new URL(setupPath, window.location.origin);
        for (const [key, value] of nextSearchParams.entries()) {
            setupUrl.searchParams.set(key, value);
        }
        setIsRedirecting(true);
        runInBackground(
            navigate(`${setupUrl.pathname}${setupUrl.search}`, { replace: true }),
            (err) => {
                setIsRedirecting(false);
                log.error('Error navigating from MFA challenge to MFA setup', err);
            }
        );
    }, [
        setupStatusQuery.data,
        paymentRequestUuid,
        returnToPayment,
        navigate,
        log,
    ]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<TotpFormData>({
        resolver: zodResolver(totpSchema),
    });

    const verifyMutation = useMutation({
        mutationFn: async (data: TotpFormData) => {
            if (selectedMethod === 'EMAIL') {
                const response = await authApi.verifyEmailMfaLogin({
                    code: data.totp_code,
                });
                return VerifyMfaLoginResponseSchema.parse(response);
            }
            if (selectedMethod === 'SMS') {
                const response = await authApi.verifySmsMfaLogin({
                    code: data.totp_code,
                });
                return VerifyMfaLoginResponseSchema.parse(response);
            }
            const response = await authApi.verifyTotp(data);
            return VerifyTotpResponseSchema.parse(response);
        },
        onSuccess: async () => {
            // Clear any stale cache from previous user session
            queryClient.clear();
            // Clear Zustand store first to ensure fresh state
            useAuthStore.getState().logout();
            setIsRedirecting(true);
            // Fetch full profile to get complete user data including organizations
            try {
                const profileResponse = await authApi.getProfile();
                if (profileResponse) {
                    const profileData =
                        SessionUserProfileDetailedResponseSchema.parse(profileResponse);
                    setUser(profileData);
                }
                window.location.assign(returnToPayment ? '/pay' : '/dashboard');
            } catch (err) {
                // If profile fetch fails, still redirect and let ProtectedRoute handle it
                // This ensures we don't get stuck on TOTP page
                log.error('Failed to fetch profile after MFA verification', err);
                window.location.assign(returnToPayment ? '/pay' : '/dashboard');
            }
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            log.error('Error verifying MFA', err);
        },
    });

    const requestCodeMutation = useMutation({
        mutationFn: async (method: Exclude<MfaMethod, 'TOTP'>) => {
            if (method === 'EMAIL') {
                return await authApi.requestEmailMfaLogin();
            }
            return await authApi.requestSmsMfaLogin();
        },
        onSuccess: (response) => {
            setError('');
            setMessage(response.message);
        },
        onError: (err: Error) => {
            setMessage('');
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
            log.error('Error requesting MFA code', err);
        },
    });

    const onSubmit = (data: TotpFormData) => {
        setError('');
        setMessage('');
        verifyMutation.mutate(data);
    };

    const selectedMethodNeedsDelivery = selectedMethod === 'EMAIL' || selectedMethod === 'SMS';

    // Show loading screen during redirect to prevent flicker
    if (isRedirecting) {
        return <MfaChallengeLoadingCard message="Signing you in..." />;
    }

    return (
        <MfaChallengeCard
            title="Verify your identity"
            availableMethods={availableMfaMethods}
            selectedMethod={selectedMethod}
            onSelectMethod={(method) => {
                setSelectedMethod(method);
                setError('');
                setMessage('');
            }}
            areMethodsDisabled={
                setupStatusQuery.isLoading ||
                verifyMutation.isPending ||
                requestCodeMutation.isPending
            }
            deliveryAction={
                selectedMethodNeedsDelivery
                    ? {
                          label:
                              selectedMethod === 'EMAIL'
                                  ? 'Send email code'
                                  : 'Send text message code',
                          pendingLabel: 'Sending...',
                          isPending: requestCodeMutation.isPending,
                          isDisabled: setupStatusQuery.isLoading || verifyMutation.isPending,
                          onClick: () => {
                              setError('');
                              setMessage('');
                              requestCodeMutation.mutate(selectedMethod);
                          },
                      }
                    : undefined
            }
            codeInputProps={register('totp_code')}
            codeInputDisabled={setupStatusQuery.isLoading || verifyMutation.isPending}
            codeError={errors.totp_code?.message}
            errorMessage={error}
            successMessage={message}
            submitLabel="Verify"
            submitPendingLabel="Verifying..."
            isSubmitPending={verifyMutation.isPending}
            isSubmitDisabled={setupStatusQuery.isLoading || verifyMutation.isPending}
            secondaryAction={{
                label: 'Back to Login',
                isDisabled: setupStatusQuery.isLoading,
                onClick: () => {
                    const nextSearchParams = new URLSearchParams();
                    if (paymentRequestUuid.length > 0) {
                        nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
                    }
                    if (returnToPayment) {
                        nextSearchParams.set('return_to_payment', '1');
                    }
                    const nextLoginPath =
                        nextSearchParams.toString().length > 0
                            ? `/login?${nextSearchParams.toString()}`
                            : '/login';
                    runInBackground(navigate(nextLoginPath), (err) => {
                        log.error('Error navigating back to login', err);
                    });
                },
            }}
            onSubmit={handleSubmit(onSubmit)}
        />
    );
}
